import React, {useState} from "react";
import styled from "styled-components";

import {colors} from "nxtconstants";
import {resetPasswordRequest} from "api";
import {WealthyValidations} from "helpers";
import useRecaptcha from "hooks/useRecaptcha";

import {
  wrapper,
  title,
  button,
  infoline
} from "../styledElements";
import {triggerToast, handleAuthError} from "helpers";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const {token, refreshToken} = useRecaptcha("forgot_password");

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setIsSuccess(false);
      const isEmailValid = WealthyValidations.validateFormField.email(email || "");
      if (!isEmailValid) {
        setIsLoading(false);
        return triggerToast("Please enter a valid email address!", "error");
      }

      let captchaToken = token;
      if (!captchaToken) {
        captchaToken = await refreshToken();
      }

      if (!captchaToken) {
        setIsLoading(false);
        return triggerToast("Captcha Failed! Please refresh the page and try again", "error");
      }

      const response = await resetPasswordRequest({email}, captchaToken);
      if (response.status === 200) {
        setIsSuccess(true);
      }
    } catch (error) {
      handleAuthError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Wrapper>
      <Title>Forgot Password</Title>
      <InfoLine>Enter your Email ID and we will send you a link to reset your password</InfoLine>
      <FieldsWrapper>
        <FormGroup>
          <InputWrapper>
            <InputField
              placeholder="Enter Email ID"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </InputWrapper>
        </FormGroup>

        {
          isSuccess ? (
            <SuccessMessage>
              ✅ Reset password link sent on Email
            </SuccessMessage>
          ) : (
            <Button
              disabled={isLoading || !email || isSuccess}
              type="submit"
              onClick={handleSubmit}
            >
              {isLoading ? "Sending..." : "Send Link"}
            </Button>
          )
        }
      </FieldsWrapper>
    </Wrapper>
  );
};

const Title = styled(title)`
  font-size: 2.4rem;
  font-weight: 700;
  font-family: Lato;
`;

const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.4rem;
  padding-top: 4.4rem;
  width: 100%;
  flex: 1;

  @media (max-width: 768px) {
    padding-top: 2.4rem;
  }
`;

const SuccessMessage = styled(infoline)`
  font-size: 1.6rem;
  color: ${colors.black};
  font-weight: 400;
  font-family: Lato;
  margin-top: auto;

  @media (max-width: 768px) {
    margin-top: 5rem;
  }
`;

const Wrapper = styled(wrapper)`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100%;
`;

const InfoLine = styled(infoline)`
  padding: 0;
  font-size: 1.4rem;
`;

const FormGroup = styled.div`
  width: 100%;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  width: 100%;

  @media (max-width: 576px) {
    width: 100%;
  }
`;

const Button = styled(button)`
  width: 100%;
  margin-top: auto;

  @media (max-width: 768px) {
    margin-bottom: 0;
    margin-top: 5rem;
  }

  &:disabled {
    background: #e0e0e0 !important;
    color: #aaa !important;
    cursor: not-allowed !important;
    border: none !important;
    opacity: 0.7;
  }
`;

const InputField = styled.input`
  background-color: #fff;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.3);
  padding: 1.4rem 1.4rem;
  font-size: 1.6rem;
  color: ${colors.black};
  width: 100%;
  font-size: 1.8rem;
  font-weight: 400;
  display: block;
  height: 5rem;
  line-height: 1;

  &.input--phone-number, &.input-email {
    width: 100%;
  }

  -moz-appearance: textfield;
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &::placeholder {
    font-size: 1.6rem;
    color: ${colors.textGray};
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export default ForgotPassword;
